import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QRCode from 'qrcode';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { BottomNav } from '../components/BottomNav';

const PLANS = [
  { id: 'credits_100', name: '100 Créditos', credits: 100, price: 9.90, popular: false },
  { id: 'credits_250', name: '250 Créditos', credits: 250, price: 19.90, popular: true },
  { id: 'credits_500', name: '500 Créditos', credits: 500, price: 34.90, popular: false },
  { id: 'credits_1000', name: '1.000 Créditos', credits: 1000, price: 59.90, popular: false },
  { id: 'credits_2500', name: '2.500 Créditos', credits: 2500, price: 129.90, popular: false },
];

const FEATURE_LABELS = {
  avatar_pulse: 'Ícone pulsante no mapa',
  banner: 'Banner em destaque',
  category_highlight: 'Destaque na categoria',
  wider_reach: 'Maior alcance',
  max_priority: 'Prioridade máxima',
};

const PLAN_ICONS = ['⚡', '📅', '🥉', '🥈', '🥇'];

/** Mapeia a resposta da API /api/boosts/plans para o formato usado no card */
function mapBoostPlan(p, idx) {
  const features = typeof p.features === 'object' && p.features !== null
    ? Object.keys(p.features).filter(k => p.features[k]).map(k => FEATURE_LABELS[k] || k)
    : [];
  // Benefícios implícitos (não estão no banco mas são efeitos reais)
  if (idx >= 1) features.unshift('Topo da lista de comércios');
  return {
    name: p.name,
    icon: PLAN_ICONS[idx] || '🚀',
    hours: p.duration_hours,
    credits: p.credits_cost,
    features,
  };
}

export function BuyCreditsPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const canvasRef = useRef(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [pixData, setPixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [boostPlans, setBoostPlans] = useState([]);

  // Carrega comércios do usuário
  const fetchBusinesses = useCallback(async () => {
    try {
      const { data } = await api.get(isAdmin ? '/admin/businesses' : '/merchant/businesses', isAdmin ? { params: { status: 'approved', limit: 50 } } : undefined);
      setBusinesses((data.data || data || []).filter(b => b.is_active && b.is_approved));
    } catch (_) { /* sem permissão = ok */ }
  }, [isAdmin]);

  // Carrega planos de boost da API (fonte única da verdade)
  const fetchBoostPlans = useCallback(async () => {
    try {
      const { data } = await api.get('/boosts/plans');
      setBoostPlans((data.data || []).map(mapBoostPlan));
    } catch (_) { /* endpoint público — em caso de erro, card fica vazio */ }
  }, []);

  useEffect(() => { fetchBusinesses(); fetchBoostPlans(); }, [fetchBusinesses, fetchBoostPlans]);

  // Timer de expiração
  useEffect(() => {
    if (!pixData?.expires_at) return;
    const timer = setInterval(() => {
      const left = Math.max(0, Math.floor((new Date(pixData.expires_at) - Date.now()) / 1000));
      setTimeLeft(left);
      if (left <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [pixData]);

  // Renderiza QR code no canvas
  useEffect(() => {
    if (!pixData?.brCode || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, pixData.brCode, {
      width: 220,
      margin: 2,
      color: { dark: 'var(--nc-text)', light: 'var(--nc-surface)' },
    }).catch(() => {});
  }, [pixData]);

  const handleBuy = async () => {
    if (!selectedPlan || !selectedBusiness) {
      setError('Selecione o estabelecimento que receberá os créditos.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        plan_id: selectedPlan.id,
        business_id: Number(selectedBusiness),
      };

      const { data } = await api.post('/payments/pix-qrcode', payload);
      setPixData(data);
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Erro ao gerar pagamento';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!pixData?.brCode) return;
    try {
      await navigator.clipboard.writeText(pixData.brCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (_) {
      // fallback: seleciona texto
      const ta = document.createElement('textarea');
      ta.value = pixData.brCode;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleNewPurchase = () => {
    setPixData(null);
    setSelectedPlan(null);
    setError(null);
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const gradientBg = 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)';

  // Tela de QR Code (pós-compra)
  if (pixData) {
    return (
      <div className="nc-page">
        <div style={{ background: gradientBg, color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <button onClick={handleNewPurchase} aria-label="Voltar" className="nc-iconbtn">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="nc-page-title">Pagar com Pix</h1>
          </div>
          <div style={{ textAlign: 'center', marginTop: 6 }}>
            <div style={{ fontSize: 26, fontWeight: 700 }}>
              R$ {pixData.plan.price.toFixed(2)}
            </div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              {pixData.plan.credits} créditos
            </div>
          </div>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* QR Code */}
          <div style={{
            background: 'var(--nc-surface)', borderRadius: 16, padding: 16, border: '2px solid var(--nc-border)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: 16
          }}>
            <canvas ref={canvasRef} style={{ display: 'block' }} />
          </div>

          {/* Timer */}
          {timeLeft > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
              fontSize: 13, color: timeLeft < 120 ? '#b91c1c' : '#6b7280',
              background: timeLeft < 120 ? '#fef2f2' : '#f3f4f6',
              padding: '6px 14px', borderRadius: 20, fontWeight: 500
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
              </svg>
              Expira em {formatTime(timeLeft)}
            </div>
          )}

          {/* Copia e Cola */}
          <button
            onClick={handleCopy}
            style={{
              width: '100%', padding: '14px', borderRadius: 12, border: `2px solid ${copied ? '#15803d' : '#7c3aed'}`,
              background: copied ? '#f0fdf4' : '#fff', color: copied ? '#15803d' : '#7c3aed',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.2s', marginBottom: 20
            }}
          >
            {copied ? (
              <>✅ Copiado!</>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                Copiar código Pix
              </>
            )}
          </button>

          {/* Instruções */}
          <div style={{
            background: '#fefce8', border: '1px solid var(--nc-warning-border)', borderRadius: 12,
            padding: 14, width: '100%', fontSize: 13, color: '#92400e', lineHeight: 1.6
          }}>
            <strong>📱 Como pagar:</strong><br />
            1. Abra o app do seu banco<br />
            2. Escolha <strong>Pix Copia e Cola</strong><br />
            3. Cole o código ou escaneie o QR<br />
            4. Após o pagamento, o administrador confirmará seus créditos em até 24h
          </div>
        </div>
      </div>
    );
  }

  // Tela de seleção de plano
  return (
    <div className="nc-page nc-page--nav">
      <div style={{ background: gradientBg, color: 'var(--nc-on-primary)', padding: '18px 16px 22px', borderRadius: '0 0 22px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={() => navigate(-1)} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="nc-page-title">Comprar créditos</h1>
        </div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>
          Créditos são usados para impulsionar seu comércio no mapa e no feed da cidade
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {error && (
          <div role="alert" style={{
            padding: 12, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13, marginBottom: 12
          }}>
            {error}
          </div>
        )}

        {/* Explicação: o que os créditos compram */}
        <div style={{
          background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)',
          padding: 16, marginBottom: 18,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 20 }}>💡</span>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--nc-text)', margin: 0 }}>
              Seus créditos compram impulsionamento
            </h2>
          </div>
          <p className="nc-hint" style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
            Ao ativar um plano, seu comércio ganha <strong style={{ color: 'var(--nc-boost)' }}>destaque no mapa</strong> com ícone pulsante
            e aparece <strong style={{ color: 'var(--nc-boost)' }}>antes dos outros</strong> na listagem da cidade.
          </p>

          {/* Grade de planos de boost */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8,
            marginBottom: 14, minHeight: boostPlans.length ? 'auto' : 60,
          }}>
            {boostPlans.length > 0 ? boostPlans.map((bp) => (
              <div key={bp.name} style={{
                background: '#f9fafb', borderRadius: 10, padding: 10,
                border: '1px solid var(--nc-surface-alt)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 16 }}>{bp.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nc-text)' }}>{bp.name}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--nc-text-secondary)', marginBottom: 4 }}>
                  {bp.hours >= 24 ? `${bp.hours / 24} dia(s)` : `${bp.hours}h`} • {bp.credits} créditos
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {bp.features.map((f, i) => (
                    <span key={i} style={{ fontSize: 10, color: 'var(--nc-text-strong)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span style={{ color: 'var(--nc-boost)', fontSize: 10, flexShrink: 0 }}>✓</span> {f}
                    </span>
                  ))}
                </div>
              </div>
            )) : (
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--nc-text-muted)', fontSize: 12, padding: '16px 0' }}>
                <Loading message="" /> Carregando planos...
              </div>
            )}
          </div>

          {/* Exemplos práticos */}
          <div style={{
            background: '#f5f3ff', borderRadius: 10, padding: 10,
            border: '1px solid #ddd6fe', fontSize: 12, color: '#4c1d95', lineHeight: 1.6,
          }}>
            <strong>📊 Exemplos práticos:</strong><br />
            <span style={{ display: 'inline-block', marginTop: 4 }}>
              • 100 créditos = <strong>5 impulsos Flash</strong> (6h cada) ou <strong>2 impulsos Diário</strong> (24h cada)<br />
              • 500 créditos = <strong>2 impulsos Prata</strong> (7 dias cada) ou <strong>1 Ouro</strong> (15 dias)<br />
              • Créditos não expiram — use quando quiser
            </span>
          </div>
        </div>

        {/* Planos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {PLANS.map(plan => {
            const isSelected = selectedPlan?.id === plan.id;
            return (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 16px', borderRadius: 12, border: `2px solid ${isSelected ? '#7c3aed' : '#e5e7eb'}`,
                  background: isSelected ? '#f5f3ff' : '#fff',
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  transition: 'all 0.15s', position: 'relative',
                }}
              >
                {plan.popular && (
                  <span style={{
                    position: 'absolute', top: -8, right: 12, background: 'var(--nc-warning)', color: 'var(--nc-on-primary)',
                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8
                  }}>
                    MAIS POPULAR
                  </span>
                )}
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--nc-text)' }}>
                    {plan.name}
                  </div>
                  <div className="nc-hint" style={{ marginTop: 2 }}>
                    {(plan.price / plan.credits * 100).toFixed(1)} centavos por crédito
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--nc-boost)' }}>
                    R$ {plan.price.toFixed(2)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Destino dos créditos */}
        {businesses.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="credits-destination" style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text-strong)', marginBottom: 6, display: 'block' }}>
              Destino dos créditos *
            </label>
            <select
              id="credits-destination"
              value={selectedBusiness}
              onChange={e => setSelectedBusiness(e.target.value)}
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #d1d5db',
                fontSize: 14, background: 'var(--nc-surface)', color: 'var(--nc-text)'
              }}
            >
              <option value="">Selecione um estabelecimento</option>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div role="alert" style={{ padding: 12, borderRadius: 10, background: 'var(--nc-warning-soft)', border: '1px solid var(--nc-warning-border)', color: '#92400e', fontSize: 13, marginBottom: 16 }}>
            Nenhum estabelecimento aprovado disponível para receber créditos.
          </div>
        )}

        <button
          onClick={handleBuy}
          disabled={!selectedPlan || !selectedBusiness || loading}
          style={{
            width: '100%', padding: '15px', borderRadius: 12,
            background: selectedPlan && selectedBusiness && !loading ? gradientBg : '#d1d5db',
            color: 'var(--nc-on-primary)', fontSize: 15, fontWeight: 700, border: 'none',
            cursor: selectedPlan && selectedBusiness && !loading ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.2s',
          }}
        >
          {loading ? (
            <Loading message="" />
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
              </svg>
              Gerar QR Code Pix
            </>
          )}
        </button>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--nc-text-muted)' }}>
          🔒 Pagamento seguro via Pix • Créditos liberados após confirmação
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
